import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Loader2, Sparkles } from "lucide-react";
import { foregroundOnSolidAccent } from "../../pages/guest_pages/subcategory_pages/shared/categoryDetailShared";

/**
 * Full-viewport gate for signed-in users who did not pick this sub-topic at registration.
 */
export default function SubcategoryInterestGateModal({
  open,
  color = "#c9a000",
  industryLabel = "",
  topicLabel = "",
  onProceed,
  onGoBack,
  pending = false,
}) {
  const titleId = useId();
  const descId = useId();
  const consentId = useId();
  const panelRef = useRef(null);
  const consentInputRef = useRef(null);
  const prevFocusRef = useRef(null);
  const [consentAccepted, setConsentAccepted] = useState(false);

  useEffect(() => {
    if (open) setConsentAccepted(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    prevFocusRef.current = document.activeElement;
    const t = window.setTimeout(() => {
      consentInputRef.current?.focus?.({ preventScroll: true });
    }, 90);
    return () => {
      window.clearTimeout(t);
      try {
        prevFocusRef.current?.focus?.();
      } catch {
        /* ignore */
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          role="presentation"
          className="fixed inset-0 z-[200] flex min-h-0 items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <motion.div
            className="absolute inset-0 bg-slate-950/55 backdrop-blur-[2px] sm:backdrop-blur-[3px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.14 }}
            aria-hidden
          />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descId}
            className="relative z-[1] box-border flex max-h-[min(calc(100dvh-1.75rem),92dvh)] w-[min(100%,calc(100vw-1.5rem))] max-w-lg flex-col min-[380px]:w-[min(100%,calc(100vw-2rem))]"
            initial={{ y: 20, opacity: 0, scale: 0.94 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 12, opacity: 0, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 560, damping: 26, mass: 0.82 }}
            style={{
              marginTop: "max(0.75rem, env(safe-area-inset-top, 0px))",
              marginBottom: "max(0.75rem, env(safe-area-inset-bottom, 0px))",
              marginLeft: "max(0.75rem, env(safe-area-inset-left, 0px))",
              marginRight: "max(0.75rem, env(safe-area-inset-right, 0px))",
            }}
          >
            <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-2xl border border-black/[0.06] bg-white shadow-[0_40px_120px_-48px_rgba(15,23,42,0.65)] ring-1 ring-black/[0.03] sm:rounded-[1.85rem]">
              <div
                className="pointer-events-none h-[3px] w-full shrink-0"
                style={{
                  background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
                }}
                aria-hidden
              />
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]">
                <div className="relative px-5 pt-6 pb-5 min-[400px]:px-6 min-[400px]:pt-7 min-[400px]:pb-6 sm:px-8 sm:pt-8 sm:pb-7">
                  <div className="flex flex-col gap-4 min-[400px]:flex-row min-[400px]:items-start min-[400px]:gap-4">
                    <div
                      className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl shadow-lg ring-2 ring-white min-[400px]:mx-0 min-[400px]:h-14 min-[400px]:w-14"
                      style={{
                        background: `linear-gradient(145deg, ${color} 0%, ${color}dd 100%)`,
                        color: foregroundOnSolidAccent(color),
                      }}
                    >
                      <Sparkles className="h-6 w-6 min-[400px]:h-7 min-[400px]:w-7" strokeWidth={2.2} aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1 text-center min-[400px]:pt-0.5 min-[400px]:text-left">
                      <p
                        id={titleId}
                        className="text-[1.05rem] font-black leading-snug tracking-tight text-slate-900 min-[400px]:text-lg sm:text-xl"
                      >
                        Follow this topic on Infoboxx?
                      </p>
                      <p
                        id={descId}
                        className="mt-2 text-[0.8125rem] leading-relaxed text-slate-600 min-[400px]:text-sm sm:text-[0.9375rem]"
                      >
                        <span className="font-semibold text-slate-800">{topicLabel}</span>
                        {industryLabel ? (
                          <>
                            {" "}
                            in <span className="font-semibold text-slate-800">{industryLabel}</span>
                          </>
                        ) : null}{" "}
                        isn&apos;t on your profile yet. Subscribe to unlock tailored guides and updates
                        for this area, or go back to browse other topics.
                      </p>
                    </div>
                  </div>

                  <div
                    aria-hidden
                    className="my-7 h-px w-full bg-gradient-to-r from-transparent via-slate-200/90 to-transparent min-[400px]:my-8"
                  />

                  <div
                    className={[
                      "rounded-2xl border-2 px-4 py-3.5 transition-all duration-300 sm:px-5",
                      consentAccepted
                        ? "border-[#FFD221]/55 bg-gradient-to-br from-[#FFD221]/[0.09] via-white to-slate-50/30 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.65)] ring-1 ring-[#FFD221]/15"
                        : "border-slate-200 bg-slate-50/85 hover:border-slate-300/95",
                    ].join(" ")}
                  >
                    <div className="flex gap-3 sm:gap-3.5">
                      <input
                        ref={consentInputRef}
                        id={consentId}
                        type="checkbox"
                        className="mt-1 h-[1.125rem] w-[1.125rem] shrink-0 cursor-pointer rounded border-slate-300 accent-[#FFD221] focus:ring-2 focus:ring-[#FFD221]/35 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed"
                        checked={consentAccepted}
                        onChange={(e) => setConsentAccepted(e.target.checked)}
                        disabled={pending}
                      />
                      <label
                        htmlFor={consentId}
                        className="min-w-0 flex-1 cursor-pointer select-none text-left text-[13px] leading-relaxed text-slate-700 min-[400px]:text-sm"
                      >
                        I agree to the{" "}
                        <a
                          href="/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-[#9a7b00] underline decoration-[#FFD221]/65 underline-offset-2 transition hover:text-black"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Terms &amp; Conditions
                        </a>{" "}
                        and consent to subscribing to guides, alerts, and related content that match{" "}
                        <span className="font-semibold text-slate-800">{topicLabel}</span>.
                      </label>
                    </div>
                    {!consentAccepted ? (
                      <p className="mt-3 border-t border-slate-200/85 pt-3 text-left text-[11px] text-slate-500 min-[400px]:pl-[calc(1.125rem+0.875rem)] min-[400px]:text-xs">
                        Confirm above to unlock &ldquo;Subscribe to this topic&rdquo;.
                      </p>
                    ) : null}
                  </div>

                  <div className="mt-6 flex flex-col gap-2.5 sm:mt-7 sm:flex-row-reverse sm:gap-3">
                    <motion.button
                      type="button"
                      disabled={pending || !consentAccepted}
                      title={
                        !consentAccepted ? "Confirm the agreement above to subscribe" : undefined
                      }
                      onClick={() => {
                        if (!consentAccepted || pending) return;
                        onProceed?.();
                      }}
                      whileHover={
                        pending || !consentAccepted ? {} : { scale: 1.01 }
                      }
                      whileTap={
                        pending || !consentAccepted ? {} : { scale: 0.99 }
                      }
                      className={[
                        "inline-flex min-h-[3rem] w-full shrink-0 items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-black transition-[box-shadow,color,background] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed sm:flex-1 sm:min-h-0 sm:px-5 sm:py-3.5",
                        pending || !consentAccepted
                          ? `bg-slate-200 text-slate-500 shadow-none focus-visible:outline-slate-400`
                          : `shadow-[0_18px_44px_-16px_rgba(15,23,42,0.28)] hover:brightness-[1.03] active:brightness-100 focus-visible:outline-[#FFD221]`,
                      ].join(" ")}
                      style={
                        pending || !consentAccepted
                          ? undefined
                          : {
                              background: color,
                              color: foregroundOnSolidAccent(color),
                            }
                      }
                    >
                      {pending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                          Subscribing…
                        </>
                      ) : (
                        <>Subscribe to this topic</>
                      )}
                    </motion.button>
                    <button
                      type="button"
                      disabled={pending}
                      onClick={onGoBack}
                      className="inline-flex min-h-[3rem] w-full shrink-0 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 sm:flex-1 sm:min-h-0 sm:px-5 sm:py-3.5"
                    >
                      <ArrowLeft className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden />
                      <span className="text-center [text-wrap:balance]">
                        Back to {industryLabel || "topics"}
                      </span>
                    </button>
                  </div>
                  {!consentAccepted && !pending ? (
                    <p className="mt-3 text-center text-[11px] font-medium tracking-tight text-slate-500">
                      Checking the consent box enables subscription.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
